import { yupResolver } from '@hookform/resolvers/yup';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';

import {
  Button,
  ConfirmationDialog,
  DropdownSearch,
  Input,
  Loading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/common';
import { AppRoutes } from '@/constants';
import { navigate } from '@/providers';
import {
  getDirtyFields,
  handleServerValidationErrors,
  parseNumber,
  showAlertSuccess,
} from '@/utils';

import {
  DEVICE_TYPE_OPTIONS,
  IPAD_CONFIG_FORM_FIELDS,
  IPAD_CONFIG_REMARKS_MAX_LENGTH,
  IPAD_CONFIG_UPDATE_SCHEMA,
  DeviceType,
  useIpadConfigMutation,
  useIpadConfigQuery,
  IIpadConfigUpdate,
  IIpadConfigDetails,
  AccessControlMode,
} from '@/features/ipad-configs';
import ProjectRoomSection from './ProjectRoomSection';

const IpadConfigUpdate = () => {
  const { id } = useParams();
  const {
    data: details,
    isFetching,
    error,
  } = useIpadConfigQuery(id || '') as UseQueryResult<IIpadConfigDetails, Error>;
  const data: IIpadConfigUpdate & Partial<IIpadConfigDetails> = {
    allowMembers: details?.members?.map((member) => member.id) || [],
    serialNumber: details?.serialNumber || '',
    ...details,
  };
  const mutation = useIpadConfigMutation(id || '');
  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors, dirtyFields },
  } = useForm<IIpadConfigUpdate>({
    resolver: yupResolver(IPAD_CONFIG_UPDATE_SCHEMA),
    values: data,
    resetOptions: {
      keepDirtyValues: true,
    },
  });
  const deviceTypeValue = watch(IPAD_CONFIG_FORM_FIELDS.deviceType);
  const accessControlModeValue = watch(IPAD_CONFIG_FORM_FIELDS.accessControlMode);
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  useEffect(() => {
    if (error) {
      navigate(AppRoutes.NOT_FOUND, { replace: true });
    }
  }, [error]);

  // Confirm that the allowed members are cleared when switching from Restricted Access to Open Access for Project Room type
  // Or switching device type from Project Room to other types with Restricted Access
  const onConfirmation = async (submitData: IIpadConfigUpdate) => {
    if (
      data.deviceType === DeviceType.ProjectRoom &&
      data.accessControlMode === AccessControlMode.RestrictedAccess &&
      (submitData.deviceType !== DeviceType.ProjectRoom ||
        submitData.accessControlMode !== AccessControlMode.RestrictedAccess)
    ) {
      return setConfirmationDialogOpen(true);
    }
    await onSubmit(submitData);
  };

  const onSubmit = async (data: IIpadConfigUpdate) => {
    const changedData = getDirtyFields(data, dirtyFields);
    changedData.serialNumber = id;

    // Build denied members for restricted Project Room type
    if (
      deviceTypeValue === DeviceType.ProjectRoom &&
      accessControlModeValue === AccessControlMode.RestrictedAccess &&
      changedData.allowMembers?.length
    ) {
      changedData.denyMembers = details?.members
        ?.filter((member) => !changedData.allowMembers?.includes(member.id))
        .map((member) => member.id);
    }

    await mutation.mutateAsync(changedData as IIpadConfigUpdate, {
      onSuccess: () => {
        navigate(AppRoutes.IPAD_CONFIGURATIONS);
        showAlertSuccess('Configuration updated successfully!');
      },
      onError: (error) => {
        handleServerValidationErrors(error, setError);
      },
    });
  };

  return (
    <div className="flex flex-col gap-base mt-xl mb-24 text-brand-primary-darkest">
      {/* Header with Title */}
      <h1 className="text-h1 font-bold">Edit Configuration</h1>

      {isFetching || !details || !data ? (
        <Loading fullScreen />
      ) : (
        <>
          <div className="rounded-softer border border-neutral-n4 bg-neutral-white">
            <div className="text-sm flex flex-col gap-y-6 p-m">
              <div className="text-xl font-bold text-brand-primary-darkest">Device Details</div>
              <DropdownSearch
                label="iPad Serial Number"
                placeholder="Select iPad Serial Number"
                disabled
                defaultLabel={data?.serialNumber}
                required
              />
              <Input
                label="Device Name"
                placeholder="N/A"
                value={data?.deviceName}
                required
                disabled
              />
              <Controller
                name={IPAD_CONFIG_FORM_FIELDS.deviceType}
                control={control}
                defaultValue={data?.deviceType}
                render={({ field }) => (
                  <Select
                    label="Device Type"
                    required
                    value={field.value?.toString()}
                    onValueChange={(value) => field.onChange(parseNumber(value))}
                    error={errors[IPAD_CONFIG_FORM_FIELDS.deviceType]?.message}
                  >
                    <SelectTrigger allowClear className="w-full">
                      <SelectValue placeholder="Select Device Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEVICE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {deviceTypeValue === DeviceType.ProjectRoom && (
                <ProjectRoomSection
                  register={register}
                  control={control}
                  watch={watch}
                  errors={errors}
                  defaultValues={details}
                />
              )}
              <Textarea
                label="Remark"
                placeholder="Enter remarks"
                className="[&_textarea]:min-h-28"
                maxLength={IPAD_CONFIG_REMARKS_MAX_LENGTH}
                error={errors[IPAD_CONFIG_FORM_FIELDS.remarks]?.message}
                {...register(IPAD_CONFIG_FORM_FIELDS.remarks)}
              />
            </div>
          </div>
          {/* back and submit button */}
          <div className="flex justify-end gap-s-plus">
            <Button
              className="w-[104px]"
              size="lg"
              variant="cancel"
              borderRadius="soft"
              onClick={() => navigate(AppRoutes.IPAD_CONFIGURATIONS)}
            >
              Back
            </Button>
            <Button
              variant="primary"
              borderRadius="soft"
              size="lg"
              className="w-[104px]"
              onClick={handleSubmit(onConfirmation)}
              disabled={mutation.isPending}
            >
              Submit
            </Button>
          </div>
          <ConfirmationDialog
            open={isConfirmationDialogOpen}
            onOpenChange={setConfirmationDialogOpen}
            onConfirm={handleSubmit(onSubmit)}
            title="Allowed Members list will be deleted!"
            subtitle="With these new changes, the data for Allowed Members will be deleted. Do you still want to proceed?"
            okButtonLabel="Yes, Proceed"
          />
        </>
      )}
    </div>
  );
};

export default IpadConfigUpdate;
